import { OrderStatus } from "@classes-service-types/order-status";
import { datadogLogs } from "@datadog/browser-logs";
import * as Sentry from "@sentry/vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import axios from "axios";
import { computed, reactive, readonly, ref, toRaw, toValue, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import { channelTypes } from "@/components/program-manager/sessions/constants";
import {
  canRegisterForPrograms,
  hasConfigurableCancellationPolicyEnabled,
  hasDisplayStudentDiscountEnabled,
  useCustomPriceFees,
} from "@/composables/authorization.js";
import billingAgreement, { isExchangeChannel } from "@/composables/useBillings";
import { useConsolidatedAsyncState } from "@/composables/useConsolidatedAsyncState";
import { useHousingQuestionnaire } from "@/composables/useHousingQuestionnaire";
import { useProgramSelectionData } from "@/composables/useProgramSelectionData";
import { useSessionFees } from "@/composables/useSessionFees";
import {
  API_ENTITY_INFORMATION,
  BILLING_PARTY_INSTITUTION,
  requiredSchool as learnerTypeIdsThatRequireSchoolSelection,
  ORDER_STATUS,
  TODOS_IDS,
  US_COLLEGE_STUDENT_ID,
  US_GRADUATE_STUDENT_ID,
} from "@/constants";
import { getApiClient } from "@/services/API";
import enrollmentService from "@/services/enrollment";
import entitiesService from "@/services/entities";
import formService from "@/services/form";
import ordersService from "@/services/orders";
import paymentService from "@/services/payment";
import profileService from "@/services/profile";
import programSessionsService from "@/services/programSessions.js";
import programPageService from "@/services/programsPage";
import { useNoApplicationFeeFlow } from "../composables/useNoApplicationFeeFlow";
import isGroupVisaEligible from "../utils/isGroupVisaEligible";
import { patchFormioObject } from "../utils/utils";
import { hasWithdrawalPolicy, resolveWithdrawalPolicyUrl } from "../utils/withdrawalPolicy";

import { useInitialOptionsData } from "./useInitialOptionsData";

const useApplication = (paramsRef) => {
  const store = useStore();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { createSubmissionDataForFormIo } = useProgramSelectionData();
  const { learnerType } = useInitialOptionsData();
  const _applicationId = ref(undefined);
  const applicationId = computed(() => {
    const localRef = _applicationId.value;
    const urlParam = toValue(paramsRef.value?.applicationId);
    return localRef ?? urlParam;
  });

  const participantId = computed(() => store.state.currentUser?.participantId);
  const isLoggedIn = computed(() => Boolean(store.getters.isLoggedIn));

  const applicationOrderQueryKey = computed(() => ["application", "orders", applicationId.value]);
  const {
    data: applicationOrders,
    isLoading: applicationOrdersAreLoading,
    isFetched: applicationOrdersAreFetched,
    error: applicationOrdersError,
  } = useQuery({
    enabled: computed(() => Boolean(applicationId.value && participantId.value)),
    queryKey: applicationOrderQueryKey, // reactive query key eliminates need for `watch`
    queryFn: async () => {
      const response = await ordersService.list({
        application_id: applicationId.value,
        participant_id: participantId.value,
        get_withdrawn: true,
        get_canceled: true,
        get_archived: true,
      });
      return response?.map((res) => res?.order) ?? [];
    },
  });

  const primaryOrder = computed(() => {
    return applicationOrders.value?.find((order) => !order?.parent_id);
  });

  const formioStudentId = computed(() => store.state.currentUser?.formioStudentId ?? "");
  const ownerId = computed(() => store.state.currentUser?.formioStudentId || store.state.currentUser?.formioAdvisorId);
  const customerId = computed(() => store.state.customer_id);
  const sessionId = computed(() => toValue(paramsRef.value?.sessionId) ?? primaryOrder.value?.session_id);
  const programPageId = computed(() => toValue(paramsRef.value?.programPageId) ?? primaryOrder.value?.program_page_id);
  const financialAid = computed(() => {
    if (applicationId.value)
      return profile.value?.financial_aid.find((financialAid) => financialAid.application_id === applicationId.value);
    return undefined;
  });

  const invalidateQueryKey = (keyParts = [""]) => {
    queryClient.invalidateQueries({ queryKey: keyParts });
  };

  // temporary data that needs to be preserved between stages (e.g. configurator -> application steps)
  // but not yet ready to persist to remote data service
  const unsavedOrder = ref(undefined);
  const stashUnsavedOrder = (order) => {
    unsavedOrder.value = toValue(order);
  };
  const unsavedLearnerTypeId = ref(undefined);
  const stashUnsavedLearnerTypeId = (id) => {
    unsavedLearnerTypeId.value = toValue(id);
  };
  const unsavedProgramStepData = ref(undefined);
  const stashUnsavedProgramStepData = (data) => {
    unsavedProgramStepData.value = toValue(data);
  };

  const unsavedHousingQuestionnaire = reactive({ questionnaire: undefined, questionnaireId: undefined });
  const resetUnsavedHousingQuestionnaire = () => {
    unsavedHousingQuestionnaire.questionnaire = undefined;
    unsavedHousingQuestionnaire.questionnaireId = undefined;
  };
  const stashUnsavedHousingQuestionnaire = (data, id) => {
    if (!data) {
      return;
    }
    unsavedHousingQuestionnaire.questionnaire = JSON.parse(JSON.stringify(toValue(data)));
    unsavedHousingQuestionnaire.questionnaireId = toValue(id);
  };
  const { createOrUpdatedHousingQuestionnaire, getHousingQuestionnaire } = useHousingQuestionnaire();

  const fetchContractsByOpeId = async (ope_id) => {
    if (!ope_id || `${ope_id}`.length === 0) {
      return [];
    }
    const formattedOpeId = `${ope_id}`.padStart(8, "0");
    const contractsResponse = await enrollmentService.loadContracts(formattedOpeId);
    return Array.isArray(contractsResponse) ? contractsResponse : [];
  };

  const applicationSubmissionKey = computed(() => ["applicationSubmissions", applicationId.value]);
  const invalidateApplicationSubmissions = () => {
    invalidateQueryKey(applicationSubmissionKey.value);
  };

  const {
    data: applicationSubmissions,
    isLoading: applicationSubmissionsAreLoading,
    error: applicationSubmissionsError,
  } = useQuery({
    enabled: computed(() => Boolean(applicationId.value)),
    queryKey: applicationSubmissionKey,
    queryFn: async () => {
      const submissions = await formService.getSubmissionReport({
        applicationIds: [applicationId.value],
      });

      return submissions.reduce((submissions, submission) => {
        // organize submissions into an object indexed by form path
        const formPath = submission.formPath;
        if (!Object.hasOwn(submissions, formPath)) {
          submissions[formPath] = [];
        }
        submissions[formPath].push(submission);
        return submissions;
      }, {});
    },
  });

  const createFinancialAidMutation = useMutation({
    mutationFn: async ({ payload }) => {
      const response = await profileService.createFinancialAid(payload);
      return response.data?.data;
    },
    onSuccess: (financialAidData) => {
      const updatedProfileData = structuredClone(profile.value);
      updatedProfileData.financial_aid = profile.value?.financial_aid?.map((each) => {
        if (each.application_id === applicationId.value) return financialAidData;
        return each;
      });
      queryClient.setQueryData(["profile", participantId.value], updatedProfileData);
    },
  });

  const createFinancialAid = async (payload) => {
    await createFinancialAidMutation.mutateAsync({ payload });
  };

  const updateFinancialAidMutation = useMutation({
    mutationFn: async ({ payload }) => {
      let response;
      if (financialAid.value) {
        response = await profileService.toggleFinancialAid(applicationId.value, payload);
        return response.data?.data;
      }
    },
    onSuccess: (financialAidData) => {
      const updatedProfileData = structuredClone(profile.value);
      updatedProfileData.financial_aid = profile.value?.financial_aid?.map((each) => {
        if (each.application_id === applicationId.value) return financialAidData;
        return each;
      });
      queryClient.setQueryData(["profile", participantId.value], updatedProfileData);
    },
  });

  const updateFinancialAid = async (payload) => {
    await updateFinancialAidMutation.mutateAsync({ payload });
  };

  const updateOrderContractMutation = useMutation({
    mutationFn: async ({ orderId, payload }) => {
      const response = await ordersService.updateOrderContract({ orderId, payload });
      return response?.data?.data?.order;
    },
    onSuccess: (updatedOrder) => {
      updateOrderQueryData(updatedOrder);
      // backend updates profiles
      invalidateQueryKey(profileQueryKey.value);
      refetchProfile();
      // order pricing is updated
      invalidateQueryKey(orderPricingQueryKey.value);
      // order details are updated
      invalidateQueryKey(applicationOrderQueryKey.value);
    },
  });

  const updateOrderContract = async ({ billing_contract_id, home_institution_id }) => {
    if (!primaryOrder.value) {
      return;
    }
    await updateOrderContractMutation.mutateAsync({
      orderId: primaryOrder.value?.id,
      payload: { billing_contract_id, home_institution_id },
    });
  };

  const profileQueryKey = computed(() => ["profile", participantId.value]);

  const {
    data: profile,
    refetch: refetchProfile,
    isLoading: profileIsLoading,
    error: profileError,
  } = useQuery({
    enabled: () => Boolean(participantId.value),
    queryKey: profileQueryKey,
    queryFn: async () => {
      const response = await profileService.getProfile(participantId.value);
      return response.data?.data;
    },
  });

  const saveProfileMutation = useMutation({
    mutationFn: async ({ participantId, payload }) => {
      const response = await profileService.updateProfile(participantId, payload);
      return response.data?.data;
    },
    onSuccess: (updatedProfileData) => {
      queryClient.setQueryData(profileQueryKey, updatedProfileData);
      // Once we no longer have reliance on the store data we can remove
      store.commit("setProfileData", updatedProfileData);
    },
  });

  const updateProfile = async (participantId, payload) => {
    await saveProfileMutation.mutateAsync({ participantId, payload });
  };

  const hasInstitution = (learnerTypeId) => {
    return [US_COLLEGE_STUDENT_ID, US_GRADUATE_STUDENT_ID].includes(learnerTypeId);
  };

  const schoolCollegeId = computed(() => profile.value?.colleges[0]?.college_id);

  const homeInstitutionQueryKey = computed(() => ["school", schoolCollegeId.value]);

  const {
    data: homeInstitution,
    isLoading: homeInstitutionIsLoading,
    error: homeInstitutionError,
  } = useQuery({
    enabled: computed(() => Boolean(schoolCollegeId.value)),
    queryKey: homeInstitutionQueryKey,
    queryFn: async () => {
      const response = await entitiesService.getEntityById(schoolCollegeId.value);
      const payload = response.data?.data?.items;
      return payload;
    },
  });

  const formioHomeInstitutionObject = (learnerTypeId, homeInstitution) => {
    if (!hasInstitution(learnerTypeId)) {
      return {};
    }
    const formattedOpeId = homeInstitution.ope_id ? `${homeInstitution.ope_id}`.padStart(8, "0") : null;
    return {
      label: homeInstitution.name ?? "",
      value: formattedOpeId,
    };
  };

  const billingContractsQueryKey = computed(() => ["billingContracts", homeInstitution.value?.ope_id ?? null]);

  const {
    data: contracts,
    isLoading: contractsLoading,
    error: contractsError,
  } = useQuery({
    enabled: computed(() => Boolean(profile.value)),
    queryKey: billingContractsQueryKey,
    queryFn: async () => fetchContractsByOpeId(homeInstitution.value?.ope_id),
  });

  const updateProfileColleges = async (learnerTypeId, updatedHomeInstitution) => {
    const requiresId = learnerTypeIdsThatRequireSchoolSelection.includes(learnerTypeId);
    if (!profile.value || (requiresId && !updatedHomeInstitution)) {
      // insufficient data
      return;
    }

    if (!canRegisterForPrograms.value) {
      // Only students get their data updated
      return;
    }

    const profileColleges = hasInstitution(learnerTypeId) ? [{ college_id: updatedHomeInstitution.id }] : [];

    const profileUpdate = {
      ...toRaw(profile.value),
      learner_type_id: learnerTypeId,
      colleges: [...profileColleges],
    };
    // Need to make sure the profile is persisted accurately before we process the order
    await updateProfile(participantId.value, profileUpdate);

    const homeInstitutionObject = formioHomeInstitutionObject(learnerTypeId, updatedHomeInstitution);
    // form: student
    if (formioStudentId.value) {
      const formioSubmissionData = [
        patchFormioObject("schoolname", homeInstitutionObject),
        patchFormioObject("student_type", learnerType(learnerTypeId)?.value), // 'value' is not pertaining to a reactive component
      ];
      await partialUpdateApplicationSubmission("student", formioStudentId.value, formioSubmissionData);
    }

    // TODO: Remove this once all functionality is in useApplication
    await store.commit("setCurrentUser", {
      ...store.state.currentUser,
      university: homeInstitutionObject,
    });

    await updateOrderBilling(learnerTypeId, updatedHomeInstitution);
  };

  const updateOrderBilling = async (learnerTypeId, updatedHomeInstitution) => {
    const requiresId = learnerTypeIdsThatRequireSchoolSelection.includes(learnerTypeId);
    if (!primaryOrder.value) {
      // insufficient data - need an order if we're to update it
      return;
    } else if (primaryOrder.value.status !== ORDER_STATUS.draft) {
      // This is a failsafe as we shouldn't be able to get here for other statuses
      // Can only update orders in draft status
      return;
    } else if (
      requiresId &&
      Boolean(updatedHomeInstitution) &&
      primaryOrder.value.home_institution_id === updatedHomeInstitution.id
    ) {
      // no change in institution
      return;
    }

    let contractId = null;
    if (hasInstitution(learnerTypeId)) {
      // manual reload of contracts
      let updatedContracts = [];
      let updatedAgreement = null;
      if (updatedHomeInstitution.ope_id) {
        updatedContracts = await fetchContractsByOpeId(updatedHomeInstitution.ope_id);
        updatedAgreement = billingAgreement(updatedContracts, session.value, {
          billingContractId: primaryOrder.value?.billing_contract_id,
        });
      }

      // update the order.  We need to persist the new institution details
      contractId = requiresId ? (updatedAgreement?.contract_number ?? null) : null;
    }
    await updateOrderContract({ billing_contract_id: contractId, home_institution_id: updatedHomeInstitution?.id });
    if (!contractId) {
      // May have special pricing that's not accounted for with the contract endpoint
      const orderUpdate = {
        ...toRaw(primaryOrder.value),
        force_recalculate: true,
      };

      await updateOrder(primaryOrder.value.id, orderUpdate, "updateOrderBilling.recalculate");
    }

    // update formio
    const formioUpdates = [];
    const homeInstitutionObject = formioHomeInstitutionObject(learnerTypeId, updatedHomeInstitution);
    // form: studentapplicationstage2step1
    if (learnerInformationSubmission.value) {
      const formioSubmissionData = [patchFormioObject("school-name", homeInstitutionObject)];
      formioUpdates.push(
        partialUpdateApplicationSubmission(
          "studentapplicationstage2step1",
          learnerInformationSubmission.value._id,
          formioSubmissionData,
        ),
      );
    }

    // form: programselection
    if (programSelectionSubmission.value) {
      const formioSubmissionData = [patchFormioObject("contractNumber", contractId ?? "")];
      formioUpdates.push(
        partialUpdateApplicationSubmission(
          "programselection",
          programSelectionSubmission.value._id,
          formioSubmissionData,
        ),
      );
    }

    if (formioUpdates.length > 0) {
      await Promise.allSettled(formioUpdates);
    }
  };

  const userDataQueryKey = computed(() => ["formioUser", participantId.value]);

  const {
    data: userData,
    isLoading: currentFormServiceIsLoading,
    error: currentFormServiceError,
  } = useQuery({
    enabled: () => Boolean(participantId.value),
    queryKey: userDataQueryKey,
    queryFn: async () => await formService.currentFormServiceUser(),
  });

  const applicationFeePaymentQueryKey = computed(() => ["applicationFeePayment", applicationId.value]);

  const {
    data: applicationFeePayment,
    isLoading: applicationFeePaymentIsLoading,
    error: applicationFeePaymentError,
  } = useQuery({
    enabled: () => Boolean(applicationId.value),
    queryKey: applicationFeePaymentQueryKey,
    queryFn: async () => {
      // Tanstack doesn't allow for undefined returns
      const feeAmount = await paymentService.getApplicationFeeAmount(applicationId.value);
      return feeAmount ?? -1;
    },
  });

  const getMostRecentSubmission = (formName) => {
    const submissions = applicationSubmissions.value?.[formName] ?? [];

    if (submissions.length < 1) {
      return undefined;
    }

    // use the most recent
    const sortedByDate = submissions.toSorted((a, b) => {
      if (a === b) {
        return 0;
      }

      return a.created < b.created ? -1 : 1;
    });

    return sortedByDate[sortedByDate.length - 1];
  };

  const programSelectionSubmission = computed(() => getMostRecentSubmission("programselection"));

  const learnerInformationSubmission = computed(() => getMostRecentSubmission("studentapplicationstage2step1"));

  const studentFormioPaymentSubmission = computed(() => {
    if (!applicationSubmissions.value) {
      return undefined;
    }
    const targetFormPaths = ["paymentform", "institutionpayment"];
    return Object.entries(applicationSubmissions.value).reduce((acc, [key, item]) => {
      if (targetFormPaths.includes(key) && item[0].data.application_id === applicationId.value) {
        acc = item[0];
      }
      return acc;
    }, {});
  });

  const updateSubmissionCache = ({ formName, submission }) => {
    queryClient.setQueryData(applicationSubmissionKey, (oldApplicationSubmissions) => {
      const currentSubmissions = oldApplicationSubmissions ?? {};
      const newApplicationSubmissions = { ...currentSubmissions };
      if (!Object.hasOwn(newApplicationSubmissions, formName)) {
        newApplicationSubmissions[formName] = [];
      }

      const existingIndex = newApplicationSubmissions[formName].findIndex((sub) => sub?._id === submission?._id);
      if (existingIndex === -1) {
        newApplicationSubmissions[formName].push(submission);
      } else {
        newApplicationSubmissions[formName][existingIndex] = submission;
      }

      return newApplicationSubmissions;
    });
  };

  // standard usage will create or update (POST or PUT)
  const saveApplicationSubmissionMutation = useMutation({
    mutationFn: async ({ formName, submissionData }) => {
      const submission = await formService.createOrUpdateSubmission(formName, submissionData, submissionData?._id);
      return { formName, submission };
    },
    onSuccess: (data) => updateSubmissionCache(data),
    onError: (error, { formName, submissionData }) => {
      error.message = `Error submitting application form ${formName}: ${error.message}`;
      Sentry.captureException(error, {
        tags: {
          participant_id: participantId.value,
          program_page_id: programPageId.value,
          session_id: sessionId.value,
          order_id: primaryOrder.value?.id,
          form_name: formName,
        },
        contexts: {
          form_submission: submissionData,
        },
      });
    },
  });

  const saveApplicationSubmission = async (formName, submissionData) => {
    const { submission } = await saveApplicationSubmissionMutation.mutateAsync({ formName, submissionData });
    return getMostRecentSubmission(formName) ?? submission;
  };

  const saveProgramSelectionSubmission = async (submission) => {
    await saveApplicationSubmission("programselection", submission);
    return programSelectionSubmission.value;
  };

  const saveLearnerInformationSubmission = async (submission) => {
    return await saveApplicationSubmission("studentapplicationstage2step1", submission);
  };

  const patchApplicationSubmissionMutation = useMutation({
    mutationFn: async ({ formName, submissionId, submissionData }) => {
      const submission = await formService.partiallyUpdateSubmission(formName, submissionId, submissionData);
      return { formName, submission };
    },
    onSuccess: (data) => updateSubmissionCache(data),
  });

  const partialUpdateApplicationSubmission = async (formName, submissionId, submissionData) => {
    await patchApplicationSubmissionMutation.mutateAsync({ formName, submissionId, submissionData });
    return getMostRecentSubmission(formName);
  };

  const {
    data: programPage,
    isLoading: programPageIsLoading,
    error: programPageError,
  } = useQuery({
    enabled: computed(() => Boolean(programPageId.value)),
    queryKey: ["programPage", programPageId.value],
    queryFn: async () => {
      const response = await programPageService.getProgramPage(programPageId.value, {
        hide_unpriced_rooms: true,
        sessions_for_students: true,
      });
      return response.data?.data?.items;
    },
  });

  const programPageSessions = computed(() => programPage.value?.sessions ?? []);

  const session = computed(() =>
    programPageSessions.value.find((programPageSession) => programPageSession?.id === sessionId.value),
  );

  const {
    sessionApplicationFee,
    sessionConfirmationDeposit,
    loading: sessionFeesLoading,
    error: sessionFeesError,
  } = useSessionFees(sessionId, primaryOrder, session);

  const appliedAgreement = computed(() =>
    billingAgreement(contracts.value, session.value, {
      billingContractId: primaryOrder.value?.billing_contract_id,
    }),
  );

  watch(
    appliedAgreement,
    (agreement) => {
      const payload = agreement && typeof agreement === "object" && Object.keys(agreement).length > 0 ? agreement : {};
      store.commit("setAppliedAgreement", payload);
      store.commit("setInstitutionPaysValue", {
        field: "applicationFee",
        value: agreement?.applicationFee === BILLING_PARTY_INSTITUTION,
      });
    },
    { deep: true, immediate: true },
  );

  const requiresApplication = computed(() => session.value?.requires_application ?? true);

  const isNonConfigurableSession = computed(() => !session.value?.is_personalized);

  const updateOrderQueryData = (updatedOrder) => {
    queryClient.setQueryData(applicationOrderQueryKey, (oldOrders = []) => {
      const updatedOrders = [...oldOrders];
      const existingIndex = updatedOrders.findIndex((order) => order.id === updatedOrder.id);

      if (existingIndex !== -1) {
        updatedOrders[existingIndex] = updatedOrder;
      } else {
        updatedOrders.push(updatedOrder);
      }

      return updatedOrders;
    });
  };

  // Explicit-intent order mutations. Each has one job and mirrors the
  // classes-service rule that POST fails on existing orders.
  // Info-level dispatch logging lets ops attribute every order mutation to
  // a stable caller without paging anyone.
  const logOrderMutationDispatch = (operation, callerHint, extras = {}) => {
    try {
      datadogLogs.logger.info(`${operation} dispatched`, {
        location: `useApplication.${operation}`,
        caller_hint: callerHint,
        application_id: applicationId.value,
        participant_id: participantId.value,
        session_id: sessionId.value,
        program_page_id: programPageId.value,
        ...extras,
      });
    } catch {
      /* logging is best-effort; never let it break the mutation */
    }
  };

  const assertReadyToCreateOrder = () => {
    if (!applicationId.value) {
      throw new Error("Cannot create order: applicationId is not set");
    }
    if (!participantId.value) {
      throw new Error("Cannot create order: participantId is not set");
    }
    if (!applicationOrdersAreFetched.value) {
      // Refuse to dispatch before we know whether an order already exists —
      // this is the race window that allowed silent duplicate creates.
      throw new Error("Cannot create order: applicationOrders query has not resolved yet");
    }
    if (primaryOrder.value) {
      const err = new Error("Cannot create order: a primary order already exists for this application");
      err.name = "OrderAlreadyExistsError";
      Sentry.captureException(err, {
        tags: {
          order_create_on_existing: true,
          application_id: applicationId.value,
          existing_order_id: primaryOrder.value.id,
        },
      });
      throw err;
    }
  };

  const domainEntityId = computed(() => store.getters.getDomainEntityId);
  // Normalize the base API entity id to "no override" (null), matching the storefront/pricing util.
  const overrideEntityId = computed(() =>
    domainEntityId.value && domainEntityId.value !== API_ENTITY_INFORMATION.id ? domainEntityId.value : null,
  );

  const buildCreateOrderPayload = (orderData) => ({
    ...(toValue(orderData) ?? {}),
    application_id: applicationId.value,
    participant_id: participantId.value,
    session_id: sessionId.value,
    program_page_id: programPageId.value,
    ...(hasDisplayStudentDiscountEnabled.value ? { special_pricing_override_entity_id: overrideEntityId.value } : {}),
  });

  const createConfigurableOrderMutation = useMutation({
    mutationFn: async ({ payload, callerHint = "unknown" }) => {
      assertReadyToCreateOrder();
      logOrderMutationDispatch("createOrder", callerHint);
      const orderPayload = buildCreateOrderPayload(payload);
      const response = await ordersService.createOrder({ payload: orderPayload });
      return response?.data?.data?.order;
    },
    onSuccess: (newOrder) => {
      if (newOrder) {
        updateOrderQueryData(newOrder);
      }
    },
  });

  const createConfigurableOrder = async (orderData, callerHint = "unknown") => {
    const resolved = toValue(orderData) ?? toValue(unsavedOrder.value) ?? {};
    if (Object.keys(resolved).length === 0) {
      throw new Error("Cannot create configurable order without personalized selections");
    }
    return await createConfigurableOrderMutation.mutateAsync({ payload: resolved, callerHint });
  };

  const createNonConfigurableOrderMutation = useMutation({
    mutationFn: async ({ payload, callerHint = "unknown" }) => {
      assertReadyToCreateOrder();
      logOrderMutationDispatch("createNonConfigurableOrder", callerHint);
      const orderPayload = buildCreateOrderPayload(payload);
      const response = await ordersService.createNonConfigurableOrder({ payload: orderPayload });
      return response?.data?.data?.order;
    },
    onSuccess: (newOrder) => {
      if (newOrder) {
        updateOrderQueryData(newOrder);
      }
    },
  });

  const createNonConfigurableOrder = async (orderData, callerHint = "unknown") => {
    const resolved = toValue(orderData) ?? {};
    return await createNonConfigurableOrderMutation.mutateAsync({ payload: resolved, callerHint });
  };

  const updateOrderMutation = useMutation({
    mutationFn: async ({ orderId, payload, callerHint = "unknown" }) => {
      if (!orderId) {
        throw new Error("Cannot update order: orderId is required");
      }
      if (!applicationId.value) {
        throw new Error("Cannot update order: applicationId is not set");
      }
      logOrderMutationDispatch("updateOrder", callerHint, { order_id: orderId });
      const orderPayload = {
        ...(toValue(payload) ?? {}),
        application_id: applicationId.value,
        force_recalculate: true,
      };
      const response = await ordersService.updateOrder({
        order_id: orderId,
        payload: orderPayload,
      });
      return response?.data?.data?.order;
    },
    onSuccess: (updatedOrder) => {
      if (updatedOrder) {
        updateOrderQueryData(updatedOrder);
      }
    },
  });

  const updateOrder = async (orderId, orderData, callerHint = "unknown") => {
    if (!orderId) {
      throw new Error("Cannot update order: orderId is required");
    }
    return await updateOrderMutation.mutateAsync({
      orderId,
      payload: toValue(orderData),
      callerHint,
    });
  };

  const housingQuestionnaireKey = computed(() => ["housingQuestionnaire", applicationId.value]);

  const {
    data: housingQuestionnaire,
    isLoading: housingQuestionnaireIsLoading,
    error: housingQuestionnaireError,
  } = useQuery({
    enabled: () => Boolean(applicationId.value),
    queryKey: housingQuestionnaireKey,
    queryFn: async () => await getHousingQuestionnaire(applicationId.value),
  });

  const saveHousingQuestionnaireMutation = useMutation({
    mutationFn: async ({ questionnaireData, questionnaireId }) => {
      return await createOrUpdatedHousingQuestionnaire(
        participantId.value,
        primaryOrder.value?.id,
        applicationId.value,
        questionnaireData,
        questionnaireId,
      );
    },
    onSuccess: (questionnaireData) => {
      if (questionnaireData) {
        queryClient.setQueryData(housingQuestionnaireKey, questionnaireData?.data?.data);
      }
    },
  });

  const saveHousingQuestionnaire = async (questionnaireData, questionnaireId) => {
    await saveHousingQuestionnaireMutation.mutateAsync({ questionnaireData, questionnaireId });
  };

  const setApplicationId = (id) => {
    _applicationId.value = id;
  };

  const applicationQueryKey = computed(() => {
    if (!applicationId.value) return undefined;
    return ["application", applicationId.value];
  });

  const {
    data: application,
    isLoading: applicationIsLoading,
    error: applicationError,
  } = useQuery({
    enabled: computed(() => !!applicationQueryKey.value),
    queryKey: applicationQueryKey,
    queryFn: async () => {
      if (!applicationId.value) return requiredFieldMissingError("application_id");
      const response = await profileService.getApplicationById({ applicationId: applicationId.value });
      if (!response) return;
      return response.data;
    },
  });

  const markConfiguratorCompletedMutation = useMutation({
    mutationFn: async () => {
      const response = await profileService.updateApplication(applicationId.value, { configurator_completed: true });
      return response?.data?.data;
    },
    onSuccess: (updatedApplication) => {
      if (updatedApplication) {
        queryClient.setQueryData(applicationQueryKey, updatedApplication);
      }
    },
  });

  const markConfiguratorCompleted = async () => {
    await markConfiguratorCompletedMutation.mutateAsync();
  };

  const isConfiguratorComplete = computed(() => {
    const unsavedOrderExists = typeof unsavedOrder.value === "object" && Object.keys(unsavedOrder.value).length > 0;
    const applicationIndicatesConfiguratorComplete = application.value?.configurator_completed;
    const sessionIsNonConfigurable = isNonConfigurableSession.value;
    // A learner information submission is only created in the apply stage,
    // which cannot be reached until the configurator is complete, so apply
    // progress outranks a stale configurator_completed flag on the record.
    const applyProgressIndicatesConfiguratorComplete = Boolean(learnerInformationSubmission.value);

    return [
      unsavedOrderExists,
      applicationIndicatesConfiguratorComplete,
      sessionIsNonConfigurable,
      applyProgressIndicatesConfiguratorComplete,
    ].some(Boolean);
  });

  const requiredFieldMissingError = (field) => {
    toast.open({
      message: `Required field not provided (${field})`,
      type: "error",
      position: "bottom",
    });
  };

  const createApplicationMutation = useMutation({
    mutationFn: async ({ payload }) => {
      if (!payload.participant_id) return requiredFieldMissingError("participant_id");
      const response = await profileService.createApplication(payload);
      return response?.data?.data;
    },
    onSuccess: (createdApplicationData) => {
      if (createdApplicationData) {
        queryClient.setQueryData(["application", createdApplicationData.id], createdApplicationData);
      }
    },
  });

  const createApplication = async (payload) => {
    const application = await createApplicationMutation.mutateAsync({ payload });
    if (application.id) {
      setApplicationId(application.id);
    }
  };
  // Before use this function need to set unsavedOrder.value outside, the same for housing questionnaire.
  const upsertApplicationOrderAndSubmission = async (configuratorCompleted = true) => {
    const promises = [];
    const hadExistingApplication = Boolean(applicationId.value);
    // only create if not been created yet
    if (!applicationId.value) {
      if (!participantId.value) {
        return requiredFieldMissingError("participant_id");
      }
      const payload = {
        participant_id: participantId.value,
        configurator_completed: configuratorCompleted,
        session_id: sessionId.value,
      };
      const application = await createApplicationMutation.mutateAsync({ payload });
      const appId = application.id;
      if (appId) {
        setApplicationId(appId);
        // Brand-new application — no orders can exist yet. Seed the orders
        // cache so the create-order pre-condition (isFetched) passes without
        // racing the list query that's about to run for this new applicationId.
        queryClient.setQueryData(["application", "orders", appId], []);

        const formioData = createSubmissionDataForFormIo(toValue(session.value), appId);
        formioData.data.contractNumber = appliedAgreement.value?.contract_number;

        const createOrderPromise = isNonConfigurableSession.value
          ? createNonConfigurableOrder(undefined, "upsertApplicationOrderAndSubmission.initial")
          : createConfigurableOrder(toValue(unsavedOrder.value), "upsertApplicationOrderAndSubmission.initial");
        promises.push(createOrderPromise, saveProgramSelectionSubmission(formioData));
      }
    } else if (session.value.is_personalized && unsavedOrder.value) {
      // applicationId already set — decide create vs update by current order state.
      if (primaryOrder.value) {
        promises.push(
          updateOrder(
            primaryOrder.value.id,
            toValue(unsavedOrder.value),
            "upsertApplicationOrderAndSubmission.personalized",
          ),
        );
      } else {
        promises.push(
          createConfigurableOrder(toValue(unsavedOrder.value), "upsertApplicationOrderAndSubmission.personalized"),
        );
      }
    }

    if (hadExistingApplication && configuratorCompleted && application.value?.configurator_completed !== true) {
      promises.push(markConfiguratorCompleted());
    }

    await Promise.all(promises);

    //  We need to save housing questionnaire after order is created to have order id.
    if (unsavedHousingQuestionnaire.questionnaire) {
      await saveHousingQuestionnaire(
        unsavedHousingQuestionnaire.questionnaire,
        unsavedHousingQuestionnaire.questionnaireId,
      );
      // Need to reset so we don't run into duplicate key issues.
      resetUnsavedHousingQuestionnaire();
    }
  };

  const saveForLater = async () => {
    await upsertApplicationOrderAndSubmission(false);
  };

  const orderPricingQueryKey = computed(() => ["orderPricing", primaryOrder.value?.id]);

  const fetchOrderPricing = async (orderPayload) => {
    return await ordersService.getOrdersPricing({ payload: orderPayload });
  };

  const {
    data: orderPricing,
    isLoading: orderPricingIsLoading,
    error: orderPricingError,
  } = useQuery({
    enabled: computed(() => Boolean(primaryOrder.value?.id)),
    queryKey: orderPricingQueryKey,
    queryFn: async () => {
      const payload = toRaw(primaryOrder.value);
      return await fetchOrderPricing(payload);
    },
  });

  const hasPaymentQueryKey = computed(() => ["hasPaymentForApplicationId", applicationId.value]);

  const invalidatePaymentForApplicationQuery = () => {
    invalidateQueryKey(hasPaymentQueryKey.value);
  };

  const {
    data: hasPaymentForApplicationId,
    refetch: recheckPaymentForApplicationId,
    isLoading: paymentForApplicationIsLoading,
    error: paymentForApplicationError,
  } = useQuery({
    enabled: computed(() => Boolean(applicationId.value)),
    queryKey: hasPaymentQueryKey,
    queryFn: () => {
      const options = {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };
      return fetch(`/api/payments/${applicationId.value}`, options)
        .then((response) => {
          if (response.status === 200) {
            return true;
          } else if (response.status === 404) {
            return false;
          }
          Sentry.captureException(error, {
            tags: {
              troubleshooting_note: `Invalid response: ${response}`,
              application_id: applicationId.value,
            },
          });
        })
        .catch((error) => {
          Sentry.captureException(error, {
            tags: {
              troubleshooting_note: "Error getting existing payments.",
              application_id: applicationId.value,
            },
          });
        });
    },
  });

  const savePaymentMutation = useMutation({
    mutationFn: async ({ payload }) => {
      const response = await getApiClient().post("/payments", {
        applicationId: applicationId.value,
        formPath: payload,
      });
      return Boolean(response?.data);
    },
    onSuccess: (succesValue) => {
      queryClient.setQueryData(hasPaymentQueryKey, succesValue);
    },
  });

  const createPayment = async (payload) => {
    return await savePaymentMutation.mutateAsync({ payload });
  };

  const opeId = computed(() => {
    const value = userData.value?.data?.schoolname?.value;
    if (!(typeof value === "string")) {
      return undefined;
    }

    return value.padStart(8, 0);
  });

  const {
    data: ipeds,
    isLoading: ipedsIsLoading,
    error: ipedsLoadError,
  } = useQuery({
    enabled: computed(() => Boolean(opeId.value) && opeId.value !== "undefined"),
    queryKey: computed(() => ["ipeds", opeId.value]),
    queryFn: async () => {
      if (opeId.value && opeId.value !== "undefined") {
        const response = await getApiClient().get(`/home-university/ipeds/${opeId.value}`);
        return response.data?.ipeds ?? null;
      }
      return null;
    },
  });

  const submitToPaymentServiceMutation = useMutation({
    mutationFn: async ({ payload }) => {
      const response = await axios.post("/api/submit-to-paymentservice", payload);
      invalidatePaymentForApplicationQuery();
      return response;
    },
  });

  const submitToPaymentService = async (payload) => {
    return await submitToPaymentServiceMutation.mutateAsync({ payload });
  };

  const validateSubmissionMutation = useMutation({
    mutationFn: async ({ formPath, body }) => {
      try {
        await formService.validateSubmission(formPath, body);
        return false;
      } catch {
        return true;
      }
    },
  });

  const validateSubmission = async ({ formPath, body }) => {
    return await validateSubmissionMutation.mutateAsync({ formPath, body });
  };

  const orderIsInDraft = computed(() => primaryOrder.value?.status === ORDER_STATUS.draft);

  const withdrawalPolicyArgs = computed(() => ({
    enabled: hasConfigurableCancellationPolicyEnabled.value,
    orderIsInDraft: orderIsInDraft.value,
    session: session.value,
    order: primaryOrder.value,
  }));

  const withdrawalPolicyUrl = computed(() => resolveWithdrawalPolicyUrl(withdrawalPolicyArgs.value));
  const showWithdrawalPolicy = computed(() => hasWithdrawalPolicy(withdrawalPolicyArgs.value));

  const isOrderEditable = computed(() =>
    [OrderStatus.Draft, OrderStatus.Submitted].includes(primaryOrder.value?.status),
  );

  const sessionTodoAssignmentsQueryKey = computed(() => ["sessionTodoAssignments", sessionId.value]);
  const {
    data: sessionTodoAssignments,
    isFetched: sessionTodoAssignmentsAreFetched,
    isLoading: sessionTodoAssignmentsAreLoading,
    error: sessionTodoAssignmentsError,
  } = useQuery({
    enabled: computed(() => Boolean(sessionId.value) && isLoggedIn.value),
    queryKey: sessionTodoAssignmentsQueryKey,
    queryFn: async () => {
      const response = await programSessionsService.getSessionTodoAssignments({
        session_id: sessionId.value,
        todo_ids: [TODOS_IDS.housingQuestionnaire],
      });
      return response?.data?.assigned_todo_ids ?? {};
    },
  });

  const consolidatedAsyncState = useConsolidatedAsyncState({
    queryLoading: {
      applicationOrdersAreLoading,
      contractsLoading,
      applicationSubmissionsAreLoading,
      profileIsLoading,
      currentFormServiceIsLoading,
      applicationFeePaymentIsLoading,
      housingQuestionnaireIsLoading,
      programPageIsLoading,
      applicationIsLoading,
      homeInstitutionIsLoading,
      orderPricingIsLoading,
      paymentForApplicationIsLoading,
      ipedsIsLoading,
      sessionFeesLoading,
      sessionTodoAssignmentsAreLoading,
    },
    queryErrors: {
      applicationOrdersError,
      contractsError,
      applicationSubmissionsError,
      profileError,
      currentFormServiceError,
      applicationFeePaymentError,
      housingQuestionnaireError,
      programPageError,
      applicationError,
      homeInstitutionError,
      orderPricingError,
      paymentForApplicationError,
      ipedsLoadError,
      sessionFeesError,
      sessionTodoAssignmentsError,
    },
    mutations: {
      createFinancialAidMutation,
      updateFinancialAidMutation,
      updateOrderContractMutation,
      saveProfileMutation,
      saveHousingQuestionnaireMutation,
      saveApplicationSubmissionMutation,
      patchApplicationSubmissionMutation,
      createConfigurableOrderMutation,
      createNonConfigurableOrderMutation,
      updateOrderMutation,
      createApplicationMutation,
      markConfiguratorCompletedMutation,
      savePaymentMutation,
      submitToPaymentServiceMutation,
      validateSubmissionMutation,
    },
  });

  const sessionFeesBlockingCheckout = computed(() => {
    if (!useCustomPriceFees.value) {
      return false;
    }
    if (sessionFeesError.value) {
      return true;
    }
    if (sessionFeesLoading.value && sessionApplicationFee.cents.value === null) {
      return true;
    }
    return false;
  });

  const { noApplicationFeeFlow } = useNoApplicationFeeFlow(primaryOrder, session);

  const isExchangeWithoutOrderContract = computed(() => {
    const isEx = isExchangeChannel(session.value);
    return isEx && !primaryOrder.value?.billing_contract_id;
  });

  const institutionPays = computed(() => {
    if (isExchangeWithoutOrderContract.value) return false;
    return appliedAgreement.value?.applicationFee === BILLING_PARTY_INSTITUTION;
  });

  const isCustomizedSession = computed(() => session.value?.channel_id === channelTypes.CUSTOM);

  // Customized sessions handle withdrawal terms offline, and a session with no cancellation
  // policy has nothing to agree to, so neither shows the acknowledgment nor requires it.
  const requiresCancellationPolicyAgreement = computed(() => !isCustomizedSession.value && showWithdrawalPolicy.value);

  const eligibleForGroupVisa = computed(() => isGroupVisaEligible(profile.value, session.value));

  return {
    ...consolidatedAsyncState,

    appliedAgreement,
    institutionPays,
    isExchangeWithoutOrderContract,

    applicationOrders,
    applicationOrdersAreLoading,
    applicationOrdersError,

    primaryOrder,
    orderIsInDraft,
    withdrawalPolicyUrl,
    requiresCancellationPolicyAgreement,
    isOrderEditable,
    createConfigurableOrder,
    createNonConfigurableOrder,
    updateOrder,
    unsavedOrder: readonly(unsavedOrder),
    stashUnsavedOrder,
    orderPricing,

    unsavedLearnerTypeId: readonly(unsavedLearnerTypeId),
    stashUnsavedLearnerTypeId,
    unsavedProgramStepData: readonly(unsavedProgramStepData),
    stashUnsavedProgramStepData,
    unsavedHousingQuestionnaire: readonly(unsavedHousingQuestionnaire),
    stashUnsavedHousingQuestionnaire,
    isConfiguratorComplete,

    programPage,
    programPageId,
    programPageIsLoading,
    programPageError,

    programPageSessions,
    session,
    sessionId,
    isCustomizedSession,

    applicationSubmissions,
    invalidateApplicationSubmissions,
    applicationSubmissionsAreLoading,
    applicationSubmissionsError,
    programSelectionSubmission,
    studentFormioPaymentSubmission,
    saveApplicationSubmission,
    saveProgramSelectionSubmission,
    learnerInformationSubmission,
    saveLearnerInformationSubmission,
    partialUpdateApplicationSubmission,
    validateSubmission,

    housingQuestionnaire,
    housingQuestionnaireIsLoading,
    housingQuestionnaireError,
    saveHousingQuestionnaire,

    profile,
    profileIsLoading,
    profileError,
    updateProfile,
    updateProfileColleges,
    homeInstitution,
    schoolCollegeId,
    customerId,
    userData,
    eligibleForGroupVisa,

    financialAid,
    createFinancialAid,
    updateFinancialAid,

    applicationId,
    setApplicationId,
    application,
    applicationIsLoading,
    applicationError,
    createApplication,
    upsertApplicationOrderAndSubmission,
    saveForLater,

    contracts,
    contractsLoading,
    contractsError,

    hasPaymentForApplicationId,
    recheckPaymentForApplicationId,
    invalidatePaymentForApplicationQuery,
    createPayment,
    submitToPaymentService,
    applicationFeePayment,
    applicationFeePaymentIsLoading,

    ipeds,

    sessionApplicationFee,
    sessionConfirmationDeposit,
    sessionFeesLoading,
    sessionFeesError,
    sessionFeesBlockingCheckout,

    isNonConfigurableSession,
    requiresApplication,
    noApplicationFeeFlow,
    ownerId,
    participantId,

    sessionTodoAssignments,
    sessionTodoAssignmentsAreFetched,
  };
};

export default useApplication;
